import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Heart } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PUBLIC_STORIES } from "../constants/testIds";

// CONTENT PROVENANCE NOTE
// -----------------------------------------------------------------------------
// Per Phase 1 Decision 4 (CRITICAL CONTENT-PROVENANCE RULE):
// The three community-member quotes previously used across the site cannot be
// confirmed as either (a) real, consented patient testimony or (b) explicitly
// sample/demo content. They are therefore treated as UNCONFIRMED and are NOT
// published on /stories as factual stories.
//
// The Stories page ships as an honest empty state with a "Share your story"
// pathway until the product owner supplies verified, consented stories.
//
// This is intentional and blocking: DO NOT convert unverified quotes into
// public patient-attributed content.
// -----------------------------------------------------------------------------

export default function Stories() {
  return (
    <div data-testid={PUBLIC_STORIES.page} className="bg-white">
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section
          data-testid={PUBLIC_STORIES.hero}
          className="pt-28 lg:pt-32 pb-14 lg:pb-20 bg-white"
        >
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12">
            <p className="btc-eyebrow mb-4">STORIES FROM THE COMMUNITY</p>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-[40px] sm:text-[52px] lg:text-[64px] text-[#0B132B] mb-6">
              Real people. <span className="text-[#1A5FAB]">Real journeys.</span>
            </h1>
            <div className="h-[3px] w-16 bg-[#2D9B4E] rounded-full mb-6" />
            <p className="text-lg sm:text-xl text-[#4B5563] leading-relaxed max-w-3xl">
              Behind every BTC connection is a person with a story worth telling — with their
              consent, in their own words. This is where those stories will live.
            </p>
          </div>
        </section>

        {/* Empty state — honest */}
        <section className="bg-[#F8F9FB] py-20 lg:py-28">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-12">
            <div
              data-testid={PUBLIC_STORIES.emptyState}
              className="bg-white rounded-3xl border border-gray-100 p-10 lg:p-14 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-[#2D9B4E]/10 flex items-center justify-center mx-auto mb-6">
                <Quote className="w-7 h-7 text-[#23803F]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B132B] tracking-tight mb-4">
                Our first published stories are on the way.
              </h2>
              <p className="text-[#4B5563] leading-relaxed max-w-xl mx-auto mb-8">
                We only publish stories with the full consent and voice of the people who lived
                them. Verified BTC stories will appear here as they are shared.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/contact"
                  data-testid={PUBLIC_STORIES.shareCta}
                  className="btc-btn-primary justify-center"
                >
                  Share your story with BTC
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/get-involved" className="btc-btn-outline justify-center">
                  Get Involved
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Why stories matter */}
        <section className="bg-white py-16 lg:py-24">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-12 text-center">
            <Heart className="w-8 h-8 text-[#23803F] mx-auto mb-6" fill="#2D9B4E" />
            <h3 className="text-2xl md:text-3xl font-extrabold text-[#0B132B] tracking-tight mb-4">
              Why stories matter here.
            </h3>
            <p className="text-[#4B5563] leading-relaxed">
              A single sentence from someone who has been there can reach a newly diagnosed patient
              in a way that a pamphlet never will. That is why BTC treats every story with care —
              consent-first, patient-first, always.
            </p>
          </div>
        </section>

        {/* Provenance note (subtle, small) */}
        <section className="bg-[#F8F9FB] pb-16">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-12">
            <p
              data-testid={PUBLIC_STORIES.provenanceNote}
              className="text-xs text-[#4B5563] leading-relaxed text-center italic"
            >
              BTC does not publish stories, names or quotations without the direct, informed
              consent of the person sharing them.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
